<template>
  <section
    class="AppLayout"
    :aria-busy="!isMounted"
  >
    <div class="AppLayout__headerContainer">
      <NativeBannerView />

      <header class="AppLayout__header">
        <RouterLink
          class="AppLayout__title"
          aria-label="Home"
          :to="{name: RouteName.home}"
        >
          <strong>
            <b>Ball</b>.app
          </strong>
        </RouterLink>
        <RouterLink
          class="AppLayout__logo"
          :to="{name: RouteName.home}"
        >
          <BallLogo />
        </RouterLink>

        <nav class="AppLayout__nav">
          <RouterLink
            v-if="isCommissioner"
            class="AppLayout__iconLink"
            :aria-label="crewRequests.length > 0
              ? `Notifications, ${crewRequests.length} pending crew ${crewRequests.length === 1 ? 'request' : 'requests'}`
              : 'Notifications'"
            :to="{name: RouteName.notifications}"
          >
            <Icon
              name="bell"
            />
            <Transition>
              <span
                v-if="crewRequests.length > 0"
                class="AppLayout__iconBadge"
                aria-hidden="true"
                data-testid="notificationsBadge"
              />
            </Transition>
          </RouterLink>
          <RouterLink
            class="AppLayout__iconLink"
            aria-label="Account"
            :to="{name: RouteName.account}"
          >
            <Icon
              name="circlePerson"
            />
          </RouterLink>
        </nav>
      </header>
    </div>

    <main class="AppLayout__main">
      <RouterView />
    </main>

    <Teleport to="body">
      <Transition>
        <SeasonModalView
          v-if="seasonModalRoute"
          :seasonId="seasonModalRoute.params.seasonId.toString()"
        />
      </Transition>

      <Transition>
        <EditSeasonModalView
          v-if="seasonEditModalRoute"
          :seasonId="seasonEditModalRoute.params.seasonId.toString()"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <EditSeasonRosterModalView
          v-if="editSeasonRosterModalRoute"
          :seasonId="editSeasonRosterModalRoute.params.seasonId.toString()"
        />
      </Transition>

      <Transition
        @beforeEnter="isRunModalTransitioning = true"
        @afterEnter="isRunModalTransitioning = false"
      >
        <RunModalView
          v-if="runModalRoute"
          :runId="runModalRoute.params.runId.toString()"
          :isTransitioning="isRunModalTransitioning"
        />
      </Transition>

      <Transition>
        <EditPlayerStatusModalView
          v-if="runPlayerStatusModalRoute"
          :runId="runPlayerStatusModalRoute.params.runId.toString()"
          :playerId="runPlayerStatusModalRoute.params.playerId.toString()"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <ScheduleRunModalView
          v-if="runNewModalRoute"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <StartSeasonModalView
          v-if="seasonNewModalRoute"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <EditRunModalView
          v-if="runEditModalRoute"
          :runId="runEditModalRoute.params.runId.toString()"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <ReviewRunModalView
          v-if="runReviewModalRoute"
          :runId="runReviewModalRoute.params.runId.toString()"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition
        @beforeEnter="isAddCourtModalTransitioning = true"
        @afterEnter="isAddCourtModalTransitioning = false"
      >
        <AddCourtModalView
          v-if="isAddCourtModalOpen"
          :isTransitioning="isAddCourtModalTransitioning"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <ManageCourtsModalView
          v-if="isCourtsManageModalOpen"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <InviteUserModalView
          v-if="isInvitePlayerModalOpen"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <FeedbackModalView
          v-if="isFeedbackModalOpen"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <JoinRunModalView
          v-if="joinRunModalRoute"
          :runInviteId="joinRunModalRoute.params.runInviteId.toString()"
        />
      </Transition>

      <Transition>
        <CrewModalView
          v-if="crewModalRoute"
          :crewId="crewModalRoute.params.crewId.toString()"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <NewCrewModalView
          v-if="isNewCrewModalOpen"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <PlayerModalView
          v-if="playerModalRoute"
          :playerId="playerModalRoute.params.playerId.toString()"
          @close="closeActiveModal"
        />
      </Transition>

      <Transition>
        <EditProfileModalView
          v-if="isEditProfileModalOpen"
        />
      </Transition>

      <Transition>
        <SupporterModalView
          v-if="isSupporterModalOpen"
        />
      </Transition>

      <AchievementTriggerView />
    </Teleport>
  </section>
</template>

<script lang="ts" setup>
import BallLogo from "@/components/BallLogo.vue";
import Icon from "@/components/Icon.vue";
import useAppTheme from "@/composables/useAppTheme";
import useLiveTime from "@/composables/useLiveTime";
import useModalRoute from "@/composables/useModalRoute";
import { RouteName } from "@/lib/router/routes";
import useAuthStore from "@/stores/useAuthStore";
import useCrewRequestStore from "@/stores/useCrewRequestStore";
import useModalStore from "@/stores/useModalStore";
import AchievementTriggerView from "@/views/AchievementTriggerView.vue";
import AddCourtModalView from "@/views/commish/AddCourtModalView.vue";
import EditRunModalView from "@/views/commish/EditRunModalView.vue";
import EditSeasonModalView from "@/views/commish/EditSeasonModalView.vue";
import EditSeasonRosterModalView from "@/views/commish/EditSeasonRosterModalView.vue";
import InviteUserModalView from "@/views/commish/InviteUserModalView.vue";
import ManageCourtsModalView from "@/views/commish/ManageCourtsModalView.vue";
import CrewModalView from "@/views/modals/CrewModalView.vue";
import EditPlayerStatusModalView from "@/views/modals/EditPlayerStatusModalView.vue";
import EditProfileModalView from "@/views/modals/EditProfileModalView.vue";
import FeedbackModalView from "@/views/modals/FeedbackModalView.vue";
import JoinRunModalView from "@/views/modals/JoinRunModalView.vue";
import NewCrewModalView from "@/views/modals/NewCrewModalView.vue";
import PlayerModalView from "@/views/modals/PlayerModalView.vue";
import ReviewRunModalView from "@/views/modals/ReviewRunModalView.vue";
import RunModalView from "@/views/modals/RunModalView.vue";
import ScheduleRunModalView from "@/views/modals/ScheduleRunModalView.vue";
import SeasonModalView from "@/views/modals/SeasonModalView.vue";
import StartSeasonModalView from "@/views/modals/StartSeasonModalView.vue";
import SupporterModalView from "@/views/modals/SupporterModalView.vue";
import NativeBannerView from "@/views/NativeBannerView.vue";
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";
import { RouterLink } from "vue-router";

const {
  closeActiveModal,
} = useModalStore();

const {
  modalRoute: seasonModalRoute,
} = useModalRoute(RouteName.seasonSchedule);

const {
  modalRoute: seasonEditModalRoute,
} = useModalRoute(RouteName.seasonEdit);

const {
  modalRoute: editSeasonRosterModalRoute,
} = useModalRoute(RouteName.seasonEditRoster);

const {
  modalRoute: runModalRoute,
} = useModalRoute(RouteName.run);

const {
  modalRoute: runPlayerStatusModalRoute,
} = useModalRoute(RouteName.runPlayerStatus);

const {
  modalRoute: runNewModalRoute,
} = useModalRoute(RouteName.runNew);

const {
  modalRoute: seasonNewModalRoute,
} = useModalRoute(RouteName.seasonNew);

const {
  modalRoute: runEditModalRoute,
} = useModalRoute(RouteName.runEdit);

const {
  isModalOpen: isAddCourtModalOpen,
} = useModalRoute(RouteName.courtNew);
const isAddCourtModalTransitioning = ref(false);

const {
  isModalOpen: isCourtsManageModalOpen,
} = useModalRoute(RouteName.courtsManage);

const {
  modalRoute: runReviewModalRoute,
} = useModalRoute(RouteName.runReview);

const {
  modalRoute: crewModalRoute,
} = useModalRoute(RouteName.crew);

const {
  isModalOpen: isNewCrewModalOpen,
} = useModalRoute(RouteName.crewNew);

const {
  modalRoute: playerModalRoute,
} = useModalRoute(RouteName.player);

const {
  isModalOpen: isEditProfileModalOpen,
} = useModalRoute(RouteName.editProfile);

const isRunModalTransitioning = ref(false);

const {
  isModalOpen: isInvitePlayerModalOpen,
} = useModalRoute(RouteName.invitePlayer);

const {
  isModalOpen: isFeedbackModalOpen,
} = useModalRoute(RouteName.feedback);

const {
  modalRoute: joinRunModalRoute,
} = useModalRoute(RouteName.joinRun);

const {
  isModalOpen: isSupporterModalOpen,
} = useModalRoute(RouteName.supporter);

const {
  theme,
  isCommissioner,
} = storeToRefs(useAuthStore());

const {
  crewRequests,
} = storeToRefs(useCrewRequestStore());

useAppTheme(theme);
useLiveTime();

const isMounted = ref(false);

onMounted(() => {
  isMounted.value = true;
});
</script>

<style lang="scss" scoped>
:global(body) {
  overflow: hidden;
  overscroll-behavior: contain;
}

:global(body:has(.AppLayout[aria-busy='false'])) {
  background: repeating-linear-gradient(to bottom, var(--themeColorPrimary), var(--themeColorPrimary) 50%, var(--themeColorNeutral0) 50%, var(--themeColorNeutral0) 100%) !important;
}

.AppLayout {
  --bannerHeight: 97px;
  --headerHeight: 68px;
  isolation: isolate;

  display: grid;
  grid-template-rows: minmax(0, 1fr);
  height: 100%;
  padding-top: var(--headerHeight);

  transition: 325ms transform var(--easeInOutCubic);

  &:has(.NativeBannerView),
  &:has(.UpdateBannerView) {
    padding-top: calc(var(--bannerHeight) + var(--headerHeight));
  }

  // TODO: solidify once we have more modals
  body:has(.DrawerModal:not(.v-leave-active)) & {
    transform: translateX(-96px);
  }
}

.AppLayout__headerContainer {
  position: fixed;
  top: 0;

  display: grid;
  width: 100%;
  padding: var(--safeAreaInsetTop) 0 0 0;

  background-color: var(--themeColorPrimary);
  color: var(--themeLightTransparent700);

  transition: 350ms background-color;
}

.AppLayout__header {
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  align-items: center;
  padding: 16px;
}

.AppLayout__title {
  font-size: 20px;
  transition: 350ms color;

  strong {
    font-weight: var(--fontWeightLight);
  }

  b {
    font-weight: var(--fontWeightMedium);
    font-stretch: var(--fontWidthExtended);
  }

  &.router-link-active {
    color: var(--themeColorNeutral0);
  }
}

.AppLayout__logo {
  justify-self: center;
  margin: -1px 0;
}

.AppLayout__nav {
  display: flex;
  gap: 20px;
  justify-self: end;
  align-items: center;
}

.AppLayout__iconLink {
  position: relative;

  margin: -10px;
  padding: 10px;

  color: inherit;

  transition: 350ms color;

  &.router-link-active {
    color: var(--themeColorNeutral0);
  }
}

.AppLayout__iconBadge {
  position: absolute;
  top: 7px;
  right: 7px;

  width: 8px;
  height: 8px;

  border-radius: 50%;
  background-color: var(--themeColorSecondary);

  transition: 250ms transform var(--easeOutBack);

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(0);
  }
}

.AppLayout__main {
  display: grid;
  min-height: 0;
  margin-top: var(--safeAreaInsetTop);
}
</style>
